import React, { useState } from "react";
import { api, formatError } from "@/api/client";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { Buildings, PencilSimple, Trash, X } from "@phosphor-icons/react";

export function TowersBar({ towers, selected, onSelect, canEdit, onEdit }) {
  return (
    <div className="mb-6" data-testid="towers-bar">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1.5"><Buildings size={14} /> Tower-wise Progress</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {towers.map((t) => (
          <button key={t.id} type="button" onClick={() => onSelect(t.id)} data-testid={`tower-card-${t.id}`}
            className={`dos-card p-4 text-left transition-colors ${selected === t.id ? "ring-2 ring-neutral-900" : "hover:bg-neutral-50"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-neutral-900 truncate" data-testid={`tower-name-${t.id}`}>{t.name}</div>
                <div className="text-[11px] text-neutral-400">Size {t.size ?? 1} · {t.activity_count} activities</div>
              </div>
              {canEdit && (
                <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onEdit(t); }} data-testid={`tower-edit-${t.id}`} className="p-1 rounded hover:bg-neutral-200 text-neutral-400"><PencilSimple size={12} /></span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden"><div className="h-full bg-neutral-900 rounded-full transition-all duration-500" style={{ width: `${t.percent}%` }} /></div>
              <span className="text-xs font-bold text-neutral-900" data-testid={`tower-percent-${t.id}`}>{t.percent}%</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function TowerModal({ mode, tower, projectId, hasTowers, isAdmin, onClose, onSaved }) {
  const confirm = useConfirm();
  const [name, setName] = useState(tower?.name || "");
  const [size, setSize] = useState(tower?.size ?? 1);
  const [existingName, setExistingName] = useState("Tower A");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (mode === "add") {
        const { data } = await api.post(`/tracker/projects/${projectId}/towers`, { name, size: parseFloat(size) || 0, existing_name: existingName });
        toast.success(`${name} added`);
        onSaved(data.created[data.created.length - 1].id);
      } else {
        await api.patch(`/tracker/towers/${tower.id}`, { name, size: parseFloat(size) || 0 });
        toast.success("Tower updated"); onSaved();
      }
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!(await confirm({ title: `Delete ${tower.name}?`, message: "This permanently deletes its checklist, progress history, photos and site visit reports.", confirmLabel: "Delete Tower", tone: "danger" }))) return;
    try { await api.delete(`/tracker/towers/${tower.id}`); toast.success("Tower deleted"); onSaved(""); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-sm fade-in" data-testid="tower-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="font-display font-semibold">{mode === "add" ? "Add Tower / Wing / Building" : "Edit Tower"}</div>
          <button type="button" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {mode === "add" && !hasTowers && (
            <div className="bg-neutral-50 rounded-md p-3">
              <label className="text-xs text-neutral-500 mb-1 block">Name for the current checklist, photos and reports</label>
              <input required value={existingName} onChange={(e) => setExistingName(e.target.value)} data-testid="tower-existing-name" className="dos-input" />
              <div className="text-[11px] text-neutral-400 mt-1">The work already tracked on this project becomes this tower.</div>
            </div>
          )}
          <div><label className="text-xs text-neutral-500 mb-1 block">{mode === "add" ? "New tower / wing name" : "Name"}</label>
            <input required autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Tower B, Wing C, Clubhouse" data-testid="tower-name-input" className="dos-input" /></div>
          <div><label className="text-xs text-neutral-500 mb-1 block">Size (weight in project progress — e.g. built-up area or no. of floors)</label>
            <input type="number" step="any" min="0" value={size} onChange={(e) => setSize(e.target.value)} data-testid="tower-size-input" className="dos-input" /></div>
          {mode === "add" && <div className="text-[11px] text-neutral-400">The new tower starts with your default activity template.</div>}
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex justify-between gap-2">
          {mode === "edit" && isAdmin ? <button type="button" onClick={del} data-testid="tower-delete-btn" className="dos-btn text-xs bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"><Trash size={12} /> Delete</button> : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
            <button type="submit" disabled={busy} data-testid="tower-save-btn" className="dos-btn dos-btn-primary">{busy ? "Saving…" : mode === "add" ? "Add" : "Save"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
